import React, { useState, useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";

export default function InteractiveShiraImage() {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isResting, setIsResting] = useState(false);
  const purpleControls = useAnimation();
  const containerRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const restTimerRef = useRef(null);

  // Start floating animation on mount and keep it running forever
  useEffect(() => {
    const startFloating = async () => {
      while (true) {
        await purpleControls.start({
          y: [0, -8, 0],
          transition: {
            duration: 3,
            ease: "easeInOut",
          },
        });
      }
    };
    startFloating();
  }, [purpleControls]);

  // Handle pointer down - start dragging
  const handlePointerDown = (e) => {
    if (!containerRef.current) return;
    
    setIsDragging(true);
    setIsResting(false);
    if (restTimerRef.current) clearTimeout(restTimerRef.current);
    
    const rect = containerRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left - position.x,
      y: e.clientY - rect.top - position.y,
    };
    
    // Add dragging cursor
    document.body.classList.remove('sculpture-hover');
    document.body.classList.add('is-dragging');
    e.preventDefault();
  };

  // Handle pointer move - update position while dragging
  useEffect(() => {
    const handlePointerMove = (e) => {
      if (!isDragging || !containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const parentRect = containerRef.current.parentElement?.getBoundingClientRect();
      if (!parentRect) return;

      // Calculate new position relative to container
      const newX = e.clientX - parentRect.left - dragOffset.current.x;
      const newY = e.clientY - parentRect.top - dragOffset.current.y;
      
      setPosition({ x: newX, y: newY });
    };

    const handlePointerUp = () => {
      if (!isDragging) return;
      
      setIsDragging(false);
      document.body.classList.remove('is-dragging');
      
      // Start rest period before returning to float
      setIsResting(true);
      if (restTimerRef.current) clearTimeout(restTimerRef.current);
      restTimerRef.current = setTimeout(() => {
        setIsResting(false);
        // Smoothly return to original position
        setPosition({ x: 0, y: 0 });
      }, 2000);
    };

    if (isDragging) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    }

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging]);

  // Handle hover
  const handleMouseEnter = () => {
    if (!isDragging) {
      setIsHovered(true);
      document.body.classList.add('sculpture-hover');
    }
  };

  const handleMouseLeave = () => {
    if (!isDragging) {
      setIsHovered(false);
      document.body.classList.remove('sculpture-hover');
    }
  };

  // Cleanup cursor classes on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('sculpture-hover');
      document.body.classList.remove('is-dragging');
      if (restTimerRef.current) clearTimeout(restTimerRef.current);
    };
  }, []);

  const isInteracting = isHovered || isDragging;

  return (
    <div
      ref={containerRef}
      data-testid="interactive-shira-image"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      {/* Draggable image container */}
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          x: position.x,
          y: position.y,
          touchAction: "none",
        }}
        animate={{
          x: position.x,
          y: position.y,
        }}
        transition={{
          type: "spring",
          damping: 20,
          stiffness: 100,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onPointerDown={handlePointerDown}
      >
        {/* Bottom layer - Green bordered image */}
        <img
          src="/images/green-shira.png"
          alt="Shira with green border"
          draggable={false}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            opacity: isInteracting ? 1 : 0,
            transition: "opacity 0.4s ease-out",
            pointerEvents: "none",
          }}
        />

        {/* Top layer - Purple silhouette image - Always animating, only opacity changes */}
        <motion.img
          src="/images/purple-shira.png"
          alt="Shira purple silhouette"
          animate={purpleControls}
          draggable={false}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            opacity: isInteracting ? 0 : 1,
            transition: "opacity 0.4s ease-out",
            pointerEvents: "none",
          }}
        />
      </motion.div>
    </div>
  );
}
